import { IConfig } from '@alberta/konexi-shared';
import { IKnowledgeBaseConfig } from '@alberta/konexi-shared/dist/general/config/knowledge-base-config';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { MenuController, Platform, ToastController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { TranslateService } from '@ngx-translate/core';
import { AuthActions, AuthObserver, IAuthAction, AuthService as MobileOAuthService } from 'ionic-appauth';
import { clamp } from 'lodash';
import { Subject } from 'rxjs';
import { filter, takeUntil, takeWhile } from 'rxjs/operators';
import { IAuthEventData } from 'src/app/common/contracts/authentication/auth-event-data';
import { MenuItem } from 'src/app/common/menu/menu-item';
import { EnvironmentService } from 'src/app/shared/services/environment/environment.service';

import { AuthService } from '../../services/auth.service';
import { ChatMessagesStatusService } from '../../services/chat/chat-messages-status.service';
import { AttachmentNotificationFilterService } from '../../services/notification-filter/attachment-notification-filter-service';
import { TenantConfigService } from '../../services/tenant-config/tenant-config.service';
import { XmasService } from '../../services/xmas.service';
import { MainMenu } from './main-menu';

@Component({
  selector: 'itl-split-pane',
  templateUrl: './split-pane.component.html',
  styleUrls: ['./split-pane.component.scss'],
})
export class SplitPaneComponent implements OnInit, OnDestroy {
  public menuItems: MenuItem[];
  public isMasterBranch: boolean;
  public branchName: string;
  public version: string;
  public showHelpPageDisturber = false;
  public showNewNotifications = false;
  public isMenuExpanded = false;
  public selectedCategory = -1;
  public userIsAuthenticated = false;
  public unreadMessages = 0;
  public unreadAgentMessages = 0;
  public numberOfNewNotifications = 0;
  public userIsChatAgent = false;
  public userHasAppointmentRights = false;
  public showCalendar = false;
  public isLoggingOut = false;
  public isMobile = true;
  public isMdrBranch = false;

  public isDuringXmasHolidays: Boolean = false;
  private knowledgeBaseConfig: IKnowledgeBaseConfig;
  private _oauthObserver: AuthObserver;
  private _ngUnsubscribe = new Subject<void>();

  constructor(
    private router: Router,
    private menu: MenuController,
    public authService: AuthService,
    private _translateService: TranslateService,
    private _platform: Platform,
    private _storage: Storage,
    private _xmaxService: XmasService,
    private _attachmentNotificationFilterService: AttachmentNotificationFilterService,
    private readonly _chatMessagesStatusService: ChatMessagesStatusService,
    private _environmentService: EnvironmentService,
    private _mobileOAuthService: MobileOAuthService,
    private _toastController: ToastController,
    private _tenantConfigService: TenantConfigService
  ) {
    this._environmentService.ready
      .then(() => {
        console.log('branch', this._environmentService.branch);
        this.isMasterBranch = this._environmentService.isProductionBranch();
        this.isMdrBranch = this._environmentService.isMdrBranch();
        this.branchName = this._environmentService.branch;
        this.version = this._environmentService.version;
      })
      .catch(err => console.error(err));
  }

  async ngOnInit() {
    await this._platform.ready();

    this.isMobile = this._platform.is('hybrid');

    const tenantConfig = await this._tenantConfigService.getTenantConfig();
    const showKnowledgeBase = await this._tenantConfigService.showKnowledgeBase();
    if (showKnowledgeBase) {
      this.knowledgeBaseConfig = await this._tenantConfigService.getKnowledgeBaseConfig();
    }
    this.showCalendar = await this._tenantConfigService.showCalendar();

    this.authService.authenticatedEventPublisher
      .pipe(takeWhile(authEventData => !authEventData.isAuthenticated))
      .subscribe({
        complete: async () => {
          this.userIsAuthenticated = true;
          this.userIsChatAgent = await this.authService.isAgent();
          await this.buildMenu(tenantConfig);
        },
      });

    // The menu is built once on auth-complete, but config-gated entries (e.g. CRM
    // Kooperationspartner) may only arrive via sync afterwards. Rebuild the menu
    // whenever the tenant config changes so newly-enabled entries appear without a relaunch.
    this._tenantConfigService.configUpdated$
      .pipe(
        // Guard against an auth race: a sync can fire configUpdated$ before
        // authenticatedEventPublisher completes. buildMenu() -> MainMenu.get() relies on
        // authService.userHasPermission, which only returns reliable results after auth-complete.
        filter(() => this.userIsAuthenticated),
        takeUntil(this._ngUnsubscribe)
      )
      .subscribe(updatedConfig => this.buildMenu(updatedConfig));
    setTimeout(async () => {
      await this.setHelpPageDisturber();
    });
    this.setNotificationDisturber();
    this._chatMessagesStatusService.getTotalUnreadAgentMessagesCount().subscribe(unreadMessages => {
      this.unreadAgentMessages = clamp(unreadMessages, 0, 999);
    });

    this.isDuringXmasHolidays = this._xmaxService.isDuringXmasHolidays;
  }

  private async buildMenu(tenantConfig: IConfig): Promise<void> {
    this.menuItems = await MainMenu.get(
      !this._platform.is('hybrid'),
      this.authService,
      this._translateService,
      this.knowledgeBaseConfig?.menuItemName,
      tenantConfig
    );
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  public onOpen() {}

  public onClose() {
    this.isMenuExpanded = false;
    this.selectedCategory = -1;
  }

  public async openPage(url: string) {
    this.onClose();
    if (url === '/knowledge-base') {
      await Browser.open({ url: this.knowledgeBaseConfig.link });
      await this.menu.close('mainNav');
      return;
    }
    if (url === '/portal') {
      await Browser.open({ url: this._environmentService.portalUrl });
      await this.menu.close('mainNav');
      return;
    }

    await this.menu.close('mainNav');
    if (url) {
      await this.router.navigateByUrl(url);
    }
    if (url === '/notifications') {
      this.showNewNotifications = false;
    }
    if (url === '/help') {
      this.showHelpPageDisturber = false;
    }
  }

  public async logout() {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    this.onClose();

    if (this._platform.is('hybrid')) {
      const logoutToast = await this._toastController.create({
        message: 'Du wirst abgemeldet...',
        position: 'bottom',
        buttons: [
          {
            text: 'Schließen',
            role: 'cancel',
          },
        ],
      });
      await logoutToast.present();
      this._oauthObserver = this._mobileOAuthService.addActionListener(action => this.onSignOutAction(action));
    }
    await this.authService.logout();
  }

  private setNotificationDisturber() {
    this.authService.authenticatedEventPublisher.subscribe((authEvent: IAuthEventData) => {
      if (!authEvent.isAuthenticated) {
        return;
      }
      this._attachmentNotificationFilterService.filter().subscribe(attachments => {
        const attachmentNotHasBeenSeen = attachments.filter(attachment => !attachment.hasBeenSeen);
        this.showNewNotifications = attachmentNotHasBeenSeen.length > 0;
        this.numberOfNewNotifications = attachmentNotHasBeenSeen.length;
      });
    });
  }

  private async onSignOutAction(action: IAuthAction) {
    this._mobileOAuthService.removeActionObserver(this._oauthObserver);

    if (action.action === AuthActions.SignOutSuccess) {
      window.location.reload();
    }
    if (action.action === AuthActions.SignOutFailed) {
      window.logger.log(`sing out failed - error: ${action.error}`);
      const toast = await this._toastController.create({
        message: action.error,
        duration: 10000,
        position: 'top',
        cssClass: 'login-toast',
      });
      await toast.present();
    }
  }
  /*
   * show "NEU" flag on side menu if help page wasnt opened since new update
   */
  private async setHelpPageDisturber() {
    const value = await this._storage.get('lastVersion');
    if (value === null) {
      this.showHelpPageDisturber = true;
    } else {
      const [majorUpdate, minorUpdate, pathUpdate] = this.version.split('.');
      const [majorStorage, minorStorage, pathStorage] = value.split('.');
      if (
        Number(majorUpdate) > Number(majorStorage) ||
        Number(minorUpdate) > Number(minorStorage) ||
        Number(pathUpdate) > Number(pathStorage)
      ) {
        this.showHelpPageDisturber = true;
      }
    }
  }
}
